import '.a{display:flex;}.ssr-plugin'
import '.b{flex-direction:column;}.ssr-plugin'
import '.w{gap:1rem;}.ssr-plugin'
import '.ch{background:none;}.ssr-plugin'
import '.ar{border:none;}.ssr-plugin'
import '.ai{cursor:pointer;}.ssr-plugin'
import '.b0{color:var(--primary-blue);}.ssr-plugin'
import '.ci{font-weight:500;}.ssr-plugin'
import '.cf{font-size:0.875rem;}.ssr-plugin'
import '.cj{padding:0.5rem 0;}.ssr-plugin'
import '.e{text-align:center;}.ssr-plugin'
import { Title } from '@app/components/core/typography/Title'
import Column from '@app/components/layout/Column'
import { i18n } from '@i18n/index'
import { useLanguage } from '@i18n/useLanguage'
import { fullName } from '@app/util/helpers'
import { ConversationCardProps, ConversationCard } from './ConversationCard'

import useMediaQuery from '@app/util/useMediaQuery'
import { useAuthState, useQuery } from '@based/sdk/react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import listConversations from 'functions/app/dashboard/communications/list-conversations/based.config'
import { Result } from 'functions/app/dashboard/communications/list-conversations'
import { Text } from '@app/components/core/typography/Text'
import { styled } from 'inlines'
import peopleDreaming from '@app/assets/images/people_dreaming.png'
import { Image } from '@app/components/core/image/Image'

type ConversationCardModel = {
  id: number
  senderId?: number
} & Omit<ConversationCardProps, 'onClick'>

const INITIAL_VISIBLE = 2

export const ConversationsPanel = ({
  conversationId,
  setActiveConverstionId,
  setActiveConversation,
}: {
  conversationId?: number
  setActiveConverstionId: (id: number) => void
  setActiveConversation: (title: string, unread: boolean) => void
}) => {
  const isMobile = useMediaQuery()
  const [language, _setLanguage] = useLanguage()
  const translations = i18n.languages[language].app.dashboard.messages
  const { userId } = useAuthState()

  const [conversations, setConversations] = useState<ConversationCardModel[]>(
    [],
  )
  const [inboxExpanded, setInboxExpanded] = useState(false)
  const [sentExpanded, setSentExpanded] = useState(false)

  const inboxHeaderRef = useRef<HTMLDivElement>(null)
  const sentHeaderRef = useRef<HTMLDivElement>(null)
  const [inboxMaxHeight, setInboxMaxHeight] = useState<string | undefined>(
    undefined,
  )
  const [sentMaxHeight, setSentMaxHeight] = useState<string | undefined>(
    undefined,
  )

  const computeMaxHeight = useCallback(
    (headerRef: React.RefObject<HTMLDivElement>) => {
      const headerEl = headerRef.current
      if (!headerEl) {
        return undefined
      }
      const rect = headerEl.getBoundingClientRect()
      const bottomPadding = 80
      return `${window.innerHeight - rect.bottom - bottomPadding}px`
    },
    [],
  )

  useEffect(() => {
    if (inboxExpanded) {
      setInboxMaxHeight(computeMaxHeight(inboxHeaderRef))
    }
  }, [inboxExpanded, computeMaxHeight])

  useEffect(() => {
    if (sentExpanded) {
      setSentMaxHeight(computeMaxHeight(sentHeaderRef))
    }
  }, [sentExpanded, computeMaxHeight])

  const { data, loading } = useQuery(listConversations.name, { userId })

  useEffect(() => {
    if (!data) {
      return
    }
    const convs: ConversationCardModel[] = data
      .filter((c: Result) => c.users?.length > 0 && c.messages?.length > 0)
      .map((c: Result) => {
        const recipient = c.users[0]
        const latestMessage = c.messages[0]
        return {
          id: c.id,
          subject: c.subject,
          recipientName: recipient ? fullName(recipient) : '',
          recipientAvatar: recipient?.avatar?.src,
          initiativeTitle: c.initiative?.title,
          initiativeImage: c.initiative?.images?.[0]?.src,
          lastMessage: new Date(latestMessage.createdAt),
          hasNewMessages:
            (latestMessage.recipients?.length ?? 0) > 0 &&
            latestMessage.recipients[0].$status === 'new',
          isOpen: c.id === conversationId,
          senderId: latestMessage.sender?.id,
        }
      })
    setConversations(convs)

    if (conversationId) {
      const conv = convs.find((c) => c.id === conversationId)
      if (conv) {
        setActiveConversation(conv.subject, conv.hasNewMessages)
      }
    }
  }, [data, conversationId, setActiveConversation])

  const inboxConversations = useMemo(() => {
    const inbox = conversations.filter(
      (c) => c.senderId !== Number(userId),
    )
    const unread = inbox
      .filter((c) => c.hasNewMessages)
      .sort((a, b) => b.lastMessage.getTime() - a.lastMessage.getTime())
    const read = inbox
      .filter((c) => !c.hasNewMessages)
      .sort((a, b) => b.lastMessage.getTime() - a.lastMessage.getTime())
    return [...unread, ...read]
  }, [conversations, userId])

  const sentConversations = useMemo(() => {
    return conversations
      .filter((c) => c.senderId === Number(userId))
      .sort((a, b) => b.lastMessage.getTime() - a.lastMessage.getTime())
  }, [conversations, userId])

  const visibleInbox = inboxExpanded
    ? inboxConversations
    : inboxConversations.slice(0, INITIAL_VISIBLE)
  const visibleSent = sentExpanded
    ? sentConversations
    : sentConversations.slice(0, INITIAL_VISIBLE)

  const selectConversation = (conv?: ConversationCardModel) => {
    if (conv) {
      setActiveConverstionId(conv.id)
      setActiveConversation(conv.subject, conv.hasNewMessages)
    } else {
      setActiveConverstionId(null)
      setActiveConversation('', false)
    }
  }

  useEffect(() => {
    setActiveConverstionId(conversationId)
  }, [conversationId, setActiveConverstionId])

  if (loading) {
    return (
      <Column
        alignItems="center"
        style={{
          justifyContent: 'center',
          flex: isMobile ? '1 1 auto' : '1 1 30rem',
          minWidth: isMobile ? 0 : '30rem',
          height: '10rem',
        }}
      >
        <Text.Medium>{translations.loading}</Text.Medium>
      </Column>
    )
  }

  if (conversations.length === 0) {
    return (
      <Column
        alignItems="center"
        gap="1rem"
        style={{
          justifyContent: 'center',
          flex: isMobile ? '1 1 auto' : '1 1 30rem',
          minWidth: isMobile ? 0 : '30rem',
          padding: '3rem 1.5rem',
        }}
      >
        <Image
          src={peopleDreaming}
          alt=""
          resizeWidth={336}
          style={{
            width: '100%',
            maxWidth: '21rem',
            height: 'auto',
            padding: '1rem',
          }}
        />
        <Title.H4 style={{ textAlign: 'center' }}>
          {translations.emptyStateTitle}
        </Title.H4>
        <Text.Medium
          style={{
            textAlign: 'center',
            maxWidth: '28rem',
            color: 'var(--shades-blue-500)',
          }}
        >
          {translations.emptyStateSubtitle}
        </Text.Medium>
      </Column>
    )
  }

  return (
    <Column
      alignItems="stretch"
      gap="1.5rem"
      style={{
        flex: isMobile ? '1 1 auto' : '1 1 30rem',
        minWidth: isMobile ? 0 : '30rem',
      }}
    >
      {inboxConversations.length > 0 && (
        <Column
          alignItems="stretch"
          style={{
            backgroundColor: 'var(--shades-white)',
            padding: '1.5rem 1rem',
            borderRadius: '1.25rem',
          }}
        >
          <styled.div ref={inboxHeaderRef}>
            <Title.H4>{translations.inbox}</Title.H4>
          </styled.div>
          <styled.div className="a b w"
            style={{
              overflowY: inboxExpanded ? 'auto' : 'hidden',
              ...(inboxExpanded && inboxMaxHeight
                ? { maxHeight: inboxMaxHeight }
                : {}),
            }}
          >
            {visibleInbox.map((conv) => (
              <ConversationCard
                {...conv}
                key={conv.id}
                onClick={() => selectConversation(conv)}
              />
            ))}
          </styled.div>
          {inboxConversations.length > INITIAL_VISIBLE && (
            <button className="ch ar ai b0 ci cf cj e"
              onClick={() => setInboxExpanded((prev) => !prev)}
            >
              {inboxExpanded ? translations.showLess : translations.showMore}
            </button>
          )}
        </Column>
      )}
      {sentConversations.length > 0 && (
        <Column
          alignItems="stretch"
          style={{
            backgroundColor: 'var(--shades-white)',
            padding: '1.5rem 1rem',
            borderRadius: '1.25rem',
          }}
        >
          <styled.div ref={sentHeaderRef}>
            <Title.H4>{translations.sentMessages}</Title.H4>
          </styled.div>
          <styled.div className="a b w"
            style={{
              overflowY: sentExpanded ? 'auto' : 'hidden',
              ...(sentExpanded && sentMaxHeight
                ? { maxHeight: sentMaxHeight }
                : {}),
            }}
          >
            {visibleSent.map((conv) => (
              <ConversationCard
                {...conv}
                key={conv.id}
                onClick={() => selectConversation(conv)}
              />
            ))}
          </styled.div>
          {sentConversations.length > INITIAL_VISIBLE && (
            <button className="ch ar ai b0 ci cf cj e"
              onClick={() => setSentExpanded((prev) => !prev)}
            >
              {sentExpanded ? translations.showLess : translations.showMore}
            </button>
          )}
        </Column>
      )}
    </Column>
  )
}
